import { i18n } from 'i18next';
import { useCallback, useEffect } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';
import { TFunction } from '../types/TFunction';
import { fetchBackendResources } from './fetchBackendResources';
import { TFunctionStore } from './tFunctionStore';

/**
 * Gets a remote-resource-based t function for a given locale from a passed
 * external store, initiating a fetch to create a new one if one does not
 * already exist
 * @param locale The locale the t function should be based upon.
 * @param packageName The name of the package the t function will draw its
 * resources from.
 * @param i18nInstance The previously initialized i18nInstance to use when
 * generating new t functions.
 * @param localTFunctions A mutable map of locales to t functions.
 * @returns The singleton t function for the given locale.
 */
export const useRemoteTFunction = (
  locale: string,
  packageName: string,
  i18nInstance: i18n,
  remoteTFunctionStore: TFunctionStore,
): TFunction | undefined => {
  const remoteTFunctions = useSyncExternalStore(
    remoteTFunctionStore.subscribe,
    remoteTFunctionStore.getSnapshot,
  );

  const fetch = useCallback(
    async (localeToFetch: string) => {
      if (remoteTFunctions.has(localeToFetch)) {
        return;
      }

      // Only fetch if the i18n workbench is known to exist on the TS.
      if (!window.head_conf?.libraryWorkbenches?.i18n) {
        return;
      }

      // Kick off a fetch of the backend resources. If one has already been
      // started, this function will return the existing promise, fulfilling the
      // request without another network request.
      const backendResources = await fetchBackendResources(packageName, localeToFetch);

      if (!remoteTFunctions.has(localeToFetch)) {
        // Overlay the remote locale onto the already loaded local one.
        i18nInstance.addResourceBundle(
          localeToFetch,
          packageName,
          backendResources ?? {},
          true,
          true,
        );

        // Add a new remote TFunction for the locale.
        remoteTFunctionStore.addTFunction(localeToFetch, i18nInstance.getFixedT(localeToFetch));
      }
    },
    [i18nInstance, packageName, remoteTFunctionStore, remoteTFunctions],
  );

  useEffect(() => {
    fetch(locale).catch(() => {});
  }, [locale, fetch]);

  return remoteTFunctions.get(locale);
};
